import { useIsTouchDevice } from "@h/useResponsive";
import { cn } from "@u/cn";
import { chipVariants } from "@ui/chip-variants";
import { Popover, PopoverContent, PopoverTrigger } from "@ui/popover";
import { Tooltip, TooltipContent, TooltipTrigger } from "@ui/tooltip";
import ResponsiveSheet from "@ui/responsive-sheet";
import { SlidersHorizontal } from "lucide-react";
import { useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";

/**
 * The one filter affordance for dashboard collections: a single "Filtros (N)"
 * button that opens every filter in a sheet, at every width.
 *
 * Replaces the row (or two) of always-visible dropdowns those pages used to
 * carry — five to eight of them owned the top of the screen, pushed the results
 * below the fold and still didn't say what was actually applied. The applied
 * state belongs in the chips bar underneath (`DashboardActiveFilters`), not in
 * the controls themselves.
 *
 * `DashboardToolbar surface="page"` has this behaviour built in; this component
 * is for the panel-surface collections (Leads, Contacts) that own their toolbar
 * slots.
 */
export function DashboardFilterPanel({
  activeCount = 0,
  className,
  children,
}: {
  /** Badge on the trigger — how many filters are currently applied. */
  activeCount?: number;
  className?: string;
  /** Filter controls, stacked inside the sheet. */
  children: ReactNode;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const isTouchDevice = useIsTouchDevice();

  const trigger = (onClick?: () => void) => (
    <button
      type="button"
      onClick={onClick}
      aria-label={t("filters", "Filtros")}
      aria-expanded={open}
      className={cn(
        chipVariants({ active: activeCount > 0, size: "sm" }),
        "relative h-11 shrink-0 gap-2 px-3 text-sm sm:h-8",
        className,
      )}
    >
      <SlidersHorizontal size={15} />
      {/* Label only once the toolbar has room (lg): between sm and lg the row
          also carries search + sort, and three labelled controls overflow. */}
      <span className="hidden lg:inline">{t("filters", "Filtros")}</span>
      {activeCount > 0 && (
        <span className="absolute -top-1.5 -right-1.5 flex size-4 items-center justify-center rounded-full bg-primary text-xs leading-none text-primary-foreground">
          {activeCount}
        </span>
      )}
    </button>
  );

  // Names the button on hover while its label is collapsed (below lg); once
  // the text shows, the tooltip would just repeat it.
  const withTip = (node: ReactNode) => (
    <Tooltip>
      <TooltipTrigger asChild>{node}</TooltipTrigger>
      <TooltipContent side="bottom" className="lg:hidden">
        {t("filters", "Filtros")}
      </TooltipContent>
    </Tooltip>
  );

  // Desktop (pointer) gets a popover anchored to the trigger — a full-height
  // right sidebar is a touch affordance, not a mouse one. Touch keeps the
  // sheet/drawer.
  if (!isTouchDevice) {
    return (
      <Popover open={open} onOpenChange={setOpen}>
        {withTip(<PopoverTrigger asChild>{trigger()}</PopoverTrigger>)}
        <PopoverContent
          align="end"
          className="w-80 max-h-[70vh] overflow-y-auto gap-5"
        >
          {children}
        </PopoverContent>
      </Popover>
    );
  }

  return (
    <>
      {withTip(trigger(() => setOpen(true)))}
      <ResponsiveSheet
        open={open}
        onOpenChange={setOpen}
        title={t("filters", "Filtros")}
      >
        <div className="flex flex-col gap-5 pt-2">{children}</div>
      </ResponsiveSheet>
    </>
  );
}
